import type { MouseEvent } from 'react';
import Box from '@liquidweb-dev/undercurrent/Box';
import ListTreeIcon from '@liquidweb-dev/undercurrent/icons/lucide/ListTree';
import Label from 'components/ServersList/ServerDetails/Label';
import ServerListDetailBox from 'components/ServersList/ServersListDetailBox';

export interface ChildServerCountProps {
	numberOfChildServers: number;
	/** Whether the VPC's VMs are shown. Only read together with `onToggle`. */
	isExpanded?: boolean;
	/** Makes the count a button that shows or hides the VPC's VMs. */
	onToggle?: () => void;
	/** Names the VPC in the button's accessible name, which starts with the visible count so a spoken "click 2 servers" matches it. */
	serverName?: string;
}

/**
 * How many VMs a Nutanix VPC has, shown on its row. With `onToggle` it is also
 * the control that expands and collapses the VPC's VMs.
 */
const ChildServerCount = ({
	numberOfChildServers,
	isExpanded = false,
	onToggle,
	serverName,
}: ChildServerCountProps) => {
	const countText = `${numberOfChildServers} ${numberOfChildServers === 1 ? 'server' : 'servers'}`;
	const content = (
		<>
			<ListTreeIcon
				sx={({ size }) => ({
					height: size.xS,
					width: size.xS,
					flexShrink: 0,
				})}
			/>
			<Label>{countText}</Label>
		</>
	);

	if (!onToggle) {
		return (
			<ServerListDetailBox sx={({ size }) => ({ minWidth: size.lg })}>
				{content}
			</ServerListDetailBox>
		);
	}

	return (
		<Box
			component="button"
			type="button"
			aria-expanded={isExpanded}
			aria-label={`${countText} in ${serverName}`}
			onClick={(e: MouseEvent<HTMLButtonElement>) => {
				// the row is a link, so keep the click from following it
				e.preventDefault();
				e.stopPropagation();
				onToggle();
			}}
			sx={({ size }) => ({
				display: 'flex',
				gap: 50,
				alignItems: 'center',
				minWidth: size.lg,
				padding: 0,
				border: 'none',
				background: 'none',
				color: 'inherit',
				font: 'inherit',
				cursor: 'pointer',
			})}
		>
			{content}
		</Box>
	);
};

export default ChildServerCount;
